import React, { useEffect, useState, useRef } from 'react';
import './ProfitSummary.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Format currency
const formatCurrency = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '$0';
  return '$' + Math.round(value).toLocaleString();
};

// Format number
const formatNumber = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return Number(value).toFixed(2);
};

// Format percentage
const formatPercent = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0.0%';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

// Get CSS class for performance-based conditional formatting
const getPerformanceClass = (value) => {
  if (value === null || value === undefined) return 'neutral';
  if (value >= 0) return 'performance-good';
  if (value >= -5) return 'performance-warning';
  return 'performance-poor';
};

// Red/yellow/green bands for attainment against a target: 0-79% red,
// 80-90% yellow, 91%+ green. Used by Effective Labor Rate, Technician
// Proficiency, and the gross margin rows.
const getAttainmentClass = (pct) => {
  if (pct === null || pct === undefined || isNaN(pct)) return 'neutral';
  if (pct >= 91) return 'performance-good';
  if (pct >= 80) return 'performance-warning';
  return 'performance-poor';
};

// Plain-language definitions shown on hover over each Sales category header.
// These say what a category covers and, just as importantly, what it doesn't —
// front end and back end are easy to conflate, and the doc fee and trade
// over/under both land somewhere people don't expect. Kept to prose: this is
// what the number means, not how the platform arrives at it.
const CATEGORY_HELP = {
  newUnits:
    'New bikes sold this month and the gross made on the machines themselves. '
    + 'Financing, F&I products, and the doc fee are counted separately under F&I Gross Profit.',
  usedUnits:
    'Used bikes sold this month and the gross made on the machines themselves, including how many were H-D Certified. '
    + 'Financing, F&I products, and the doc fee are counted separately under F&I Gross Profit.',
  frontEnd:
    'Gross made on the machines themselves—what you sold them for, less what they cost you, '
    + 'less any amount given over book value on a trade. '
    + 'Financing, F&I products, and the doc fee are not included here.',
  fi:
    'Back-end gross—what F&I earned on the deals after the bike (frontend): '
    + 'financing reserve, F&I products, and the doc fee where one is charged.'
};

// A category header with its explanation attached. The text is real DOM rather
// than a `title` attribute so it appears on hover immediately and can be styled
// to read as a paragraph; tabIndex + aria-describedby put it within reach of the
// keyboard and screen readers, which a `title` never was.
const CategoryHeader = ({ label, help, colSpan }) => {
  const tipId = `help-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  return (
    <tr className="category-row">
      <td colSpan={colSpan}>
        <span className="help-label" tabIndex={0} aria-describedby={tipId}>
          {label}
          <span className="help-tip" role="tooltip" id={tipId}>{help}</span>
        </span>
      </td>
    </tr>
  );
};

// Attainment of an actual against its target, as a percentage.
const attainmentPct = (actual, target) => {
  if (actual === null || actual === undefined) return null;
  if (!target) return null;
  return (actual / target) * 100;
};

// Benchmarks come from each dealership's own targets, so a row label can only
// name a number when every dealership in view is held to the same one.
const sharedBenchmark = (dealerships, key) => {
  const values = dealerships.map(d => d.metrics?.[key]).filter(v => v != null);
  if (values.length === 0 || values.length !== dealerships.length) return null;
  return values.every(v => v === values[0]) ? values[0] : null;
};

// Whole numbers stay whole (3, not 3.00); a fractional benchmark keeps one decimal.
const formatBenchmarkCount = (value) =>
  Number(value).toLocaleString('en-US', { maximumFractionDigits: 1 });

// Service measures a transaction in hours booked on the repair order, so its
// benchmark names the unit: "(Benchmark: 3 hours per repair order)".
const formatBenchmarkHours = (value) =>
  `${formatBenchmarkCount(value)} hours per repair order`;

// "Average Dollars Per Transaction (Benchmark: $150)", or "(Benchmark: varies)"
// when the dealerships in view are set to different values.
const benchmarkLabel = (label, value, format) =>
  `${label} (Benchmark: ${value != null ? format(value) : 'varies'})`;

export default function ProfitSummaryPage({ user, managedOwner }) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const monthPickerRef = useRef(null);

  const isOwner = user?.role === 'OWNER' || managedOwner;
  // Platform staff see how a metric is calculated; everyone else sees only where
  // its inputs came from, so the formulas stay internal.
  const isPlatformStaff = user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER';
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Close month picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
    };

    if (showMonthPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMonthPicker]);

  // Fetch dealerships on mount
  useEffect(() => {
    fetchDealerships();
  }, [managedOwner]);

  // Fetch summary data when dealerships or month changes
  useEffect(() => {
    if (dealerships.length > 0) {
      fetchSummaryData();
    }
  }, [selectedDealerships, selectedMonth, dealerships]);

  const fetchDealerships = async () => {
    try {
      const token = localStorage.getItem('token');
      let endpoint = isOwner
        ? `${API_URL}/api/owners/dealerships`
        : `${API_URL}/api/dealerships`;

      if (managedOwner && isOwner) {
        endpoint += `?ownerId=${managedOwner.id}`;
      }

      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const fetchedDealerships = data.dealerships || [];

        // Sort by displayOrder, then by name
        const sortedDealerships = [...fetchedDealerships].sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

        setDealerships(sortedDealerships);
        // Initially select all dealerships (no filter = show all)
        setSelectedDealerships([]);
      }
    } catch (error) {
      console.error('Error fetching dealerships:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // If no specific dealerships selected, fetch all
      const dealershipsToFetch = selectedDealerships.length > 0
        ? selectedDealerships
        : dealerships.map(d => d.id);

      const dealershipIdsParam = dealershipsToFetch.map(id => `dealershipIds[]=${id}`).join('&');
      const monthStr = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
      const url = `${API_URL}/api/profit-summary?${dealershipIdsParam}&month=${monthStr}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDealership = (dealershipId) => {
    setSelectedDealerships(prev => {
      if (prev.includes(dealershipId)) {
        return prev.filter(id => id !== dealershipId);
      } else {
        return [...prev, dealershipId];
      }
    });
  };

  const selectAllDealerships = () => {
    setSelectedDealerships([]);
  };

  const clearAllDealerships = () => {
    setSelectedDealerships(dealerships.map(d => d.id));
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(displayYear, monthIndex, 1);
    setSelectedMonth(newDate);
    setShowMonthPicker(false);
  };

  const handleCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setDisplayYear(now.getFullYear());
    setShowMonthPicker(false);
  };

  const formatMonthDisplay = () => {
    return `${fullMonthNames[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`;
  };

  // Get filtered dealerships and maintain displayOrder sorting
  const filteredDealerships = (() => {
    const summaryDealerships = summaryData?.dealerships || [];
    // Sort by displayOrder (use the order from the dealerships state)
    return summaryDealerships.sort((a, b) => {
      const aOrder = dealerships.find(d => d.id === a.id)?.displayOrder ?? 999;
      const bOrder = dealerships.find(d => d.id === b.id)?.displayOrder ?? 999;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  })();

  if (loading && !summaryData) {
    return (
      <div className="dashboard-view">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Performance Metrics</h2>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
            Loading Performance Metrics...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-view">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Performance Metrics</h2>
          <p className="card-subtitle">Month-to-date performance metrics across all fixed operations</p>
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          <button
            className="filter-button"
            onClick={() => setShowFilterPanel(!showFilterPanel)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1.5 3.5h13M1.5 8h13M1.5 12.5h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Filters {selectedDealerships.length > 0 ? `(${selectedDealerships.length})` : '(All)'}
          </button>

          {/* Month Picker */}
          <div className="date-picker-container" ref={monthPickerRef}>
            <button
              className="date-picker-button"
              onClick={() => setShowMonthPicker(!showMonthPicker)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="2" y="3" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M2 6h12M5 2v2M11 2v2" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              {formatMonthDisplay()}
            </button>

            {showMonthPicker && (
              <div className="date-picker-dropdown month-picker-dropdown">
                <div className="calendar-header">
                  <button onClick={() => setDisplayYear(displayYear - 1)}>‹</button>
                  <span>{displayYear}</span>
                  <button
                    onClick={() => setDisplayYear(displayYear + 1)}
                    disabled={displayYear >= new Date().getFullYear()}
                  >›</button>
                </div>
                <div className="month-grid">
                  {monthNames.map((month, index) => {
                    const isSelected = index === selectedMonth.getMonth() && displayYear === selectedMonth.getFullYear();
                    const isFuture = displayYear > new Date().getFullYear() ||
                                   (displayYear === new Date().getFullYear() && index > new Date().getMonth());
                    return (
                      <button
                        key={month}
                        className={`month-cell ${isSelected ? 'selected' : ''} ${isFuture ? 'disabled' : ''}`}
                        onClick={() => !isFuture && handleMonthSelect(index)}
                        disabled={isFuture}
                      >
                        {month}
                      </button>
                    );
                  })}
                </div>
                <button className="today-button" onClick={handleCurrentMonth}>
                  Current Month
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Active Filters */}
        {selectedDealerships.length > 0 && (
          <div className="active-filters">
            {selectedDealerships.map(id => {
              const d = dealerships.find(ds => ds.id === id);
              return d ? (
                <span key={id} className="active-filter-chip">
                  {d.name}
                  <button onClick={() => toggleDealership(id)}>×</button>
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Filter Panel */}
        {showFilterPanel && (
          <>
            <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
            <div className="filter-panel open">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Filter Dealerships</h3>
                <button onClick={() => setShowFilterPanel(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button className="filter-action-btn" onClick={selectAllDealerships}>Select All</button>
                <button className="filter-action-btn" onClick={clearAllDealerships}>Clear All</button>
              </div>
              <div className="filter-dealerships-list">
                {dealerships.map(d => (
                  <label key={d.id} className="filter-dealership-item">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.length === 0 || selectedDealerships.includes(d.id)}
                      onChange={() => toggleDealership(d.id)}
                    />
                    <span>{d.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Table */}
        {filteredDealerships.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
            No dealerships to display.
          </div>
        ) : (
          <div className="profit-summary-container">
            <table className="profit-summary-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {filteredDealerships.map(d => (
                    <th key={d.id}>{d.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Sales Section */}
                <tr className="section-header-row">
                  <td colSpan={filteredDealerships.length + 1}>Sales</td>
                </tr>
                <CategoryHeader label="New Units" help={CATEGORY_HELP.newUnits} colSpan={filteredDealerships.length + 1} />
                <tr>
                  <td>New Units Sold (count)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.newUnits.unitsSold}</td>
                  ))}
                </tr>
                <tr>
                  <td>New Units Forecast - MTD (count)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatNumber(d.metrics.newUnits.unitsTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>New Units Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.newUnits.unitsVariancePct)}>
                      {d.metrics.newUnits.unitsVariancePct !== null ? formatPercent(d.metrics.newUnits.unitsVariancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>New Unit Sales ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.newUnits.sales)}</td>
                  ))}
                </tr>
                <tr>
                  <td>New Unit Cost of Sales ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.newUnits.cost)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.newUnits.grossMargin)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Forecast - MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.newUnits.grossMarginTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.newUnits.grossMarginVariancePct)}>
                      {d.metrics.newUnits.grossMarginVariancePct !== null ? formatPercent(d.metrics.newUnits.grossMarginVariancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.newUnits.grossMarginPct)}>
                      {formatNumber(d.metrics.newUnits.grossMarginPct)}%
                    </td>
                  ))}
                </tr>

                {/* Used Units */}
                <CategoryHeader label="Used Units" help={CATEGORY_HELP.usedUnits} colSpan={filteredDealerships.length + 1} />
                <tr>
                  <td>Used Units Sold (count)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.usedUnits.unitsSold}</td>
                  ))}
                </tr>
                <tr>
                  <td>Used Units Forecast - MTD (count)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatNumber(d.metrics.usedUnits.unitsTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Used Units Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.usedUnits.unitsVariancePct)}>
                      {d.metrics.usedUnits.unitsVariancePct !== null ? formatPercent(d.metrics.usedUnits.unitsVariancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Used Unit Sales ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.usedUnits.sales)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Used Unit Cost of Sales ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.usedUnits.cost)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.usedUnits.grossMargin)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Forecast - MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.usedUnits.grossMarginTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.usedUnits.grossMarginVariancePct)}>
                      {d.metrics.usedUnits.grossMarginVariancePct !== null ? formatPercent(d.metrics.usedUnits.grossMarginVariancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.usedUnits.grossMarginPct)}>
                      {formatNumber(d.metrics.usedUnits.grossMarginPct)}%
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>% Certified</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.usedUnits.certifiedPct !== null && d.metrics.usedUnits.certifiedPct !== undefined ? formatNumber(d.metrics.usedUnits.certifiedPct) + '%' : 'Not Available'}</td>
                  ))}
                </tr>

                {/* Front End Gross — new + used unit gross, in total and per unit.
                    Same figures the Sales Performance page shows as "Total Frontend $"
                    and "Frontend per Unit". */}
                <CategoryHeader label="Front End Gross Profit" help={CATEGORY_HELP.frontEnd} colSpan={filteredDealerships.length + 1} />
                <tr>
                  <td>Total Frontend $ - New</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.frontEnd?.newGP)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Total Frontend $ - Used</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.frontEnd?.usedGP)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Total Frontend $</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.frontEnd?.totalGP)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Frontend Forecast - MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.frontEnd?.totalGPTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Frontend Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.frontEnd?.totalGPVariancePct)}>
                      {d.metrics.frontEnd?.totalGPVariancePct != null ? formatPercent(d.metrics.frontEnd.totalGPVariancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Frontend % YOY</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.frontEnd?.totalGPYoyPct)}>
                      {d.metrics.frontEnd?.totalGPYoyPct != null ? formatPercent(d.metrics.frontEnd.totalGPYoyPct) : 'Not Available'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Avg Frontend per Unit - New</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      title={d.metrics.frontEnd?.newPerUnitTarget != null ? `Target: ${formatCurrency(d.metrics.frontEnd.newPerUnitTarget)}` : undefined}
                    >
                      {d.metrics.frontEnd?.newPerUnit != null ? formatCurrency(d.metrics.frontEnd.newPerUnit) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Avg Frontend per Unit - Used</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      title={d.metrics.frontEnd?.usedPerUnitTarget != null ? `Target: ${formatCurrency(d.metrics.frontEnd.usedPerUnitTarget)}` : undefined}
                    >
                      {d.metrics.frontEnd?.usedPerUnit != null ? formatCurrency(d.metrics.frontEnd.usedPerUnit) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Avg Frontend per Unit</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      title={d.metrics.frontEnd?.perUnitTarget != null ? `Target: ${formatCurrency(d.metrics.frontEnd.perUnitTarget)}` : undefined}
                    >
                      {d.metrics.frontEnd?.perUnit != null ? formatCurrency(d.metrics.frontEnd.perUnit) : 'N/A'}
                    </td>
                  ))}
                </tr>

                {/* F&I (Finance & Insurance) */}
                <CategoryHeader label="F&I Gross Profit" help={CATEGORY_HELP.fi} colSpan={filteredDealerships.length + 1} />
                <tr>
                  <td>F&I Reserve GP ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.fi?.reserveGP)}</td>
                  ))}
                </tr>
                <tr>
                  <td>F&I Product GP ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.fi?.productGP)}</td>
                  ))}
                </tr>
                <tr>
                  <td>F&I Total ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.fi?.totalGP)}</td>
                  ))}
                </tr>
                <tr>
                  <td>F&I Forecast - MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.fi?.totalGPTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>F&I Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.fi?.totalGPVariancePct)}>
                      {d.metrics.fi?.totalGPVariancePct !== null && d.metrics.fi?.totalGPVariancePct !== undefined ? formatPercent(d.metrics.fi.totalGPVariancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>F&I % YOY</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.fi?.totalGPYoyPct)}>
                      {d.metrics.fi?.totalGPYoyPct != null ? formatPercent(d.metrics.fi.totalGPYoyPct) : 'Not Available'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Avg Backend per Unit—F&I Per Unit Sold ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.fi?.perUnitSold)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Average Left on Table per Bike ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.fi?.avgLeftOnTablePerBike)}</td>
                  ))}
                </tr>
                <tr>
                  {/* Positive = over-allowance given away on trades, which is
                      why the good/bad colouring runs opposite the other rows. */}
                  <td>Average Over/Under per Bike ($)</td>
                  {filteredDealerships.map(d => {
                    const ou = d.metrics.fi?.avgOverUnderPerBike;
                    return (
                      <td
                        key={d.id}
                        className={ou === null || ou === undefined ? '' : ou > 0 ? 'negative' : ou < 0 ? 'positive' : ''}
                      >
                        {ou === null || ou === undefined ? 'N/A' : formatCurrency(ou)}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>Deals with a Trade (#)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.fi?.dealsWithTrade ?? 0}</td>
                  ))}
                </tr>

                {/* Sales NPS Section - Only show if any dealership has data */}
                {filteredDealerships.some(d => d.metrics.salesNps12M !== null || d.metrics.salesNpsMTD !== null) && (
                  <>
                    <tr className="category-row">
                      <td colSpan={filteredDealerships.length + 1}>Customer Experience Index (CXI)</td>
                    </tr>
                    <tr>
                      <td>Net Promoter Score - NPS - 12M</td>
                      {filteredDealerships.map(d => (
                        <td key={d.id}>{d.metrics.salesNps12M !== null ? formatNumber(d.metrics.salesNps12M) + '%' : 'Not Available'}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>Net Promoter Score - NPS - MTD</td>
                      {filteredDealerships.map(d => (
                        <td key={d.id}>{d.metrics.salesNpsMTD !== null ? formatNumber(d.metrics.salesNpsMTD) + '%' : 'Not Available'}</td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Service Section */}
                <tr className="section-header-row">
                  <td colSpan={filteredDealerships.length + 1}>Service</td>
                </tr>
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Operations Performance</td>
                </tr>
                <tr>
                  <td>Service Revenue Actuals - MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.serviceNetSales.mtdActual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Service Revenue Forecast - MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.serviceNetSales.mtdTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Service Revenue Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.serviceNetSales.variancePct)}>
                      {d.metrics.serviceNetSales.variancePct !== null ? formatPercent(d.metrics.serviceNetSales.variancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Service GP Actuals MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.serviceGP.mtdActual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Service GP Forecast MTD ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.serviceGP.mtdTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Service GP Variance (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.serviceGP.variancePct)}>
                      {d.metrics.serviceGP.variancePct !== null ? formatPercent(d.metrics.serviceGP.variancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Service GP % YOY (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.serviceGP.yoyPct)}>
                      {d.metrics.serviceGP.yoyPct !== null ? formatPercent(d.metrics.serviceGP.yoyPct) : 'Not Available'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Percentage (%)</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.serviceGrossMargin, d.metrics.serviceGrossMarginTarget))}
                      title={d.metrics.serviceGrossMarginTarget != null ? `Target: ${formatNumber(d.metrics.serviceGrossMarginTarget)}%` : undefined}
                    >
                      {formatNumber(d.metrics.serviceGrossMargin)}%
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Parts Sold on Repair Orders ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.servicePartsOnROs > 0 ? formatCurrency(d.metrics.servicePartsOnROs) : 'Not Available'}</td>
                  ))}
                </tr>
                <tr>
                  <td>{benchmarkLabel('Parts to Labor Ratio', sharedBenchmark(filteredDealerships, 'partsToLaborRatioBenchmark'), formatCurrency)}</td>
                  {filteredDealerships.map(d => {
                    // Parts dollars sold per labor hour booked on repair orders.
                    const parts = d.metrics.servicePartsOnROs;
                    const hours = d.metrics.serviceLaborHours;
                    const benchmark = d.metrics.partsToLaborRatioBenchmark;
                    let title = benchmark != null ? `Benchmark: ${formatCurrency(benchmark)}` : '';
                    if (d.metrics.partsToLaborRatio != null) {
                      title += `—${formatCurrency(parts)} in parts across ${formatNumber(hours)} labor hours`;
                    } else if (!(parts > 0)) {
                      title += '—no parts sold on repair orders entered for this period';
                    } else {
                      title += '—no hours logged on repair orders for this period';
                    }
                    return (
                      <td
                        key={d.id}
                        className={getAttainmentClass(attainmentPct(d.metrics.partsToLaborRatio, benchmark))}
                        title={title || undefined}
                      >
                        {d.metrics.partsToLaborRatio != null ? '$' + d.metrics.partsToLaborRatio.toFixed(0) : 'Not Available'}
                      </td>
                    );
                  })}
                </tr>

                {/* Service Transactions — hours booked per repair order */}
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Transactions</td>
                </tr>
                <tr>
                  <td>{benchmarkLabel('Average Line Item Per Transaction', sharedBenchmark(filteredDealerships, 'serviceHoursPerROBenchmark'), formatBenchmarkHours)}</td>
                  {filteredDealerships.map(d => {
                    const hours = d.metrics.serviceAvgHoursPerRO;
                    const benchmark = d.metrics.serviceHoursPerROBenchmark;
                    let title = benchmark != null ? `Benchmark: ${formatBenchmarkHours(benchmark)}` : '';
                    if (hours != null) {
                      title += `—${formatNumber(d.metrics.serviceLaborHours)} hours across `
                        + `${d.metrics.serviceRepairOrders} repair order${d.metrics.serviceRepairOrders === 1 ? '' : 's'}`;
                    } else {
                      title += '—no repair orders logged for this period';
                    }
                    return (
                      <td
                        key={d.id}
                        className={getAttainmentClass(attainmentPct(hours, benchmark))}
                        title={title || undefined}
                      >
                        {hours != null ? formatNumber(hours) : 'Not Available'}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>{benchmarkLabel('Average Dollars Per Transaction', sharedBenchmark(filteredDealerships, 'serviceDollarsPerROBenchmark'), formatCurrency)}</td>
                  {filteredDealerships.map(d => {
                    const dollars = d.metrics.serviceAvgDollarsPerRO;
                    const benchmark = d.metrics.serviceDollarsPerROBenchmark;
                    // The benchmark is the hours benchmark at the posted rate, so
                    // the tooltip names both rather than a bare dollar figure.
                    let title = benchmark != null
                      ? `Benchmark: ${formatBenchmarkCount(d.metrics.serviceHoursPerROBenchmark)} hours at `
                        + `${formatCurrency(d.metrics.laborRateTarget)}`
                      : 'No labor rate set in Targets';
                    if (dollars == null && benchmark != null) {
                      title += '—no repair orders logged for this period';
                    }
                    return (
                      <td
                        key={d.id}
                        className={getAttainmentClass(attainmentPct(dollars, benchmark))}
                        title={title}
                      >
                        {dollars != null ? formatCurrency(dollars) : 'Not Available'}
                      </td>
                    );
                  })}
                </tr>

                {/* Open ROs Section */}
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Open Repair Orders</td>
                </tr>
                <tr>
                  <td>Number of Customer Open RO's</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.openCustomerROs.actual !== null ? d.metrics.openCustomerROs.actual : 'Not Available'}</td>
                  ))}
                </tr>
                <tr>
                  <td>Number of Internal Open RO's</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.openInternalROs.actual !== null ? d.metrics.openInternalROs.actual : 'Not Available'}</td>
                  ))}
                </tr>
                <tr>
                  <td>P&A Sales on open RO's ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.openROsPartsSales !== null && d.metrics.openROsPartsSales !== undefined ? formatCurrency(d.metrics.openROsPartsSales) : 'Not Available'}</td>
                  ))}
                </tr>
                <tr>
                  <td>Labor Sales on open RO's ($)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.openROsLaborSales !== null && d.metrics.openROsLaborSales !== undefined ? formatCurrency(d.metrics.openROsLaborSales) : 'Not Available'}</td>
                  ))}
                </tr>
                <tr>
                  <td>Effective Labor Rate ($)</td>
                  {filteredDealerships.map(d => {
                    const elr = d.metrics.effectiveLaborRate;
                    const cap = d.metrics.laborCapacity;
                    // Source of the technician count only — no formula. Platform staff
                    // additionally get the arithmetic behind the rate.
                    let title;
                    if (!cap || !(cap.totalHours > 0)) {
                      title = 'Not enough information to calculate this rate';
                    } else if (cap.technicianSource === 'reported') {
                      title = `Based on Daily Technicians’ Performance—averaged across `
                        + `${cap.daysReported} day${cap.daysReported === 1 ? '' : 's'} logged this period`;
                    } else if (cap.daysReported > 0) {
                      // Some days were logged, just not enough of them to describe
                      // the period, so the Targets headcount carries the rate.
                      title = `Estimated from the technician count in Targets—Daily Technicians’ Performance `
                        + `was logged on only ${cap.daysReported} of ${cap.workingDays} working days this period`;
                    } else {
                      title = 'Estimated from the technician count in Targets—no Daily Technicians’ Performance entries logged this period';
                    }
                    if (isPlatformStaff && cap && cap.totalHours > 0) {
                      title += `\n${formatCurrency(d.metrics.serviceNetSales.mtdActual)} service revenue ÷ ${Math.round(cap.totalHours)} hours `
                        + `(${cap.workingDays} working days × ${cap.hoursPerTechPerDay} hrs × ${formatNumber(cap.technicians)} techs)`;
                    }
                    return (
                      <td
                        key={d.id}
                        className={getAttainmentClass(d.metrics.laborRateAttainmentPct)}
                        title={title}
                      >
                        {elr != null ? formatCurrency(elr) : 'Not Available'}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>Technician Proficiency (%)</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getAttainmentClass(d.metrics.technicianProficiency)}>
                      {d.metrics.technicianProficiency != null ? formatNumber(d.metrics.technicianProficiency) + '%' : '0.00%'}
                    </td>
                  ))}
                </tr>

                {/* NPS Section - Only show if any dealership has data */}
                {filteredDealerships.some(d => d.metrics.nps12M !== null || d.metrics.npsMTD !== null) && (
                  <>
                    <tr className="category-row">
                      <td colSpan={filteredDealerships.length + 1}>Customer Experience Index (CXI)</td>
                    </tr>
                    <tr>
                      <td>Net Promoter Score - NPS - 12M</td>
                      {filteredDealerships.map(d => (
                        <td key={d.id}>{d.metrics.nps12M !== null ? formatNumber(d.metrics.nps12M) + '%' : 'Not Available'}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>Net Promoter Score - NPS - MTD</td>
                      {filteredDealerships.map(d => (
                        <td key={d.id}>{d.metrics.npsMTD !== null ? formatNumber(d.metrics.npsMTD) + '%' : 'Not Available'}</td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Parts Section */}
                <tr className="section-header-row">
                  <td colSpan={filteredDealerships.length + 1}>Parts & Accessories (P&A)</td>
                </tr>
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Parts & Accessories Performance</td>
                </tr>
                <tr>
                  <td>P&A Revenue Actuals - MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsNetSales.mtdActual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>P&A Revenue Forecast - MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsNetSales.mtdTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>P&A Revenue Variance %</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.partsNetSales.variancePct)}>
                      {d.metrics.partsNetSales.variancePct !== null ? formatPercent(d.metrics.partsNetSales.variancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>P&A GP Actuals MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsGP.mtdActual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>P&A GP Forecast MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsGP.mtdTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>P&A GP Variance %</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.partsGP.variancePct)}>
                      {d.metrics.partsGP.variancePct !== null ? formatPercent(d.metrics.partsGP.variancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>P&A GP % YOY</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.partsGP.yoyPct)}>
                      {d.metrics.partsGP.yoyPct !== null ? formatPercent(d.metrics.partsGP.yoyPct) : 'Not Available'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Percentage</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.partsGrossMargin, d.metrics.partsGrossMarginTarget))}
                      title={d.metrics.partsGrossMarginTarget != null ? `Target: ${formatNumber(d.metrics.partsGrossMarginTarget)}%` : undefined}
                    >
                      {formatNumber(d.metrics.partsGrossMargin)}%
                    </td>
                  ))}
                </tr>

                {/* Parts Transactions */}
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Transactions</td>
                </tr>
                <tr>
                  <td>{benchmarkLabel('Average Line Item Per Transaction', sharedBenchmark(filteredDealerships, 'partsAvgLineItemsBenchmark'), formatBenchmarkCount)}</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.partsAvgLineItems, d.metrics.partsAvgLineItemsBenchmark))}
                      title={d.metrics.partsAvgLineItemsBenchmark != null ? `Benchmark: ${formatBenchmarkCount(d.metrics.partsAvgLineItemsBenchmark)}` : undefined}
                    >
                      {formatNumber(d.metrics.partsAvgLineItems)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{benchmarkLabel('Average Dollars Per Transaction', sharedBenchmark(filteredDealerships, 'partsAvgDollarsBenchmark'), formatCurrency)}</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.partsAvgDollars, d.metrics.partsAvgDollarsBenchmark))}
                      title={d.metrics.partsAvgDollarsBenchmark != null ? `Benchmark: ${formatCurrency(d.metrics.partsAvgDollarsBenchmark)}` : undefined}
                    >
                      {formatCurrency(d.metrics.partsAvgDollars)}
                    </td>
                  ))}
                </tr>

                {/* Parts Inventory */}
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Inventory Metrics</td>
                </tr>
                <tr>
                  <td>Inventory Level Targets</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsInventory.target)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Inventory Actuals</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsInventory.actual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Special Order & Layaway</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsInventory.specialOrder)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Parts on Service Repair Orders</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{d.metrics.partsInventory.partsOnServiceRO != null ? formatCurrency(d.metrics.partsInventory.partsOnServiceRO) : '$0'}</td>
                  ))}
                </tr>
                <tr>
                  <td>On-Hand Inventory</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.partsInventory.onHand)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Inventory Turns</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatNumber(d.metrics.partsInventory.turns)}</td>
                  ))}
                </tr>

                {/* Apparel Section */}
                <tr className="section-header-row">
                  <td colSpan={filteredDealerships.length + 1}>Apparel & Licensing</td>
                </tr>
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Apparel & Licensing Performance</td>
                </tr>
                <tr>
                  <td>A&L Revenue Actuals - MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelNetSales.mtdActual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>A&L Revenue Forecast - MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelNetSales.mtdTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>A&L Revenue Variance %</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.apparelNetSales.variancePct)}>
                      {d.metrics.apparelNetSales.variancePct !== null ? formatPercent(d.metrics.apparelNetSales.variancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>A&L GP Actuals MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelGP.mtdActual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>A&L GP Forecast MTD</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelGP.mtdTarget)}</td>
                  ))}
                </tr>
                <tr>
                  <td>A&L GP Variance %</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.apparelGP.variancePct)}>
                      {d.metrics.apparelGP.variancePct !== null ? formatPercent(d.metrics.apparelGP.variancePct) : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>A&L GP % YOY</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={getPerformanceClass(d.metrics.apparelGP.yoyPct)}>
                      {d.metrics.apparelGP.yoyPct !== null ? formatPercent(d.metrics.apparelGP.yoyPct) : 'Not Available'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Gross Margin Percentage</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.apparelGrossMargin, d.metrics.apparelGrossMarginTarget))}
                      title={d.metrics.apparelGrossMarginTarget != null ? `Target: ${formatNumber(d.metrics.apparelGrossMarginTarget)}%` : undefined}
                    >
                      {formatNumber(d.metrics.apparelGrossMargin)}%
                    </td>
                  ))}
                </tr>

                {/* Apparel Transactions */}
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Transactions</td>
                </tr>
                <tr>
                  <td>{benchmarkLabel('Average Line Item Per Transaction', sharedBenchmark(filteredDealerships, 'apparelAvgLineItemsBenchmark'), formatBenchmarkCount)}</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.apparelAvgLineItems, d.metrics.apparelAvgLineItemsBenchmark))}
                      title={d.metrics.apparelAvgLineItemsBenchmark != null ? `Benchmark: ${formatBenchmarkCount(d.metrics.apparelAvgLineItemsBenchmark)}` : undefined}
                    >
                      {formatNumber(d.metrics.apparelAvgLineItems)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{benchmarkLabel('Average Dollars Per Transaction', sharedBenchmark(filteredDealerships, 'apparelAvgDollarsBenchmark'), formatCurrency)}</td>
                  {filteredDealerships.map(d => (
                    <td
                      key={d.id}
                      className={getAttainmentClass(attainmentPct(d.metrics.apparelAvgDollars, d.metrics.apparelAvgDollarsBenchmark))}
                      title={d.metrics.apparelAvgDollarsBenchmark != null ? `Benchmark: ${formatCurrency(d.metrics.apparelAvgDollarsBenchmark)}` : undefined}
                    >
                      {formatCurrency(d.metrics.apparelAvgDollars)}
                    </td>
                  ))}
                </tr>

                {/* Apparel Inventory */}
                <tr className="category-row">
                  <td colSpan={filteredDealerships.length + 1}>Inventory</td>
                </tr>
                <tr>
                  <td>Inventory Level Targets</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelInventory.target)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Inventory Actuals</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelInventory.actual)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Special Order & Layaway</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelInventory.specialOrder)}</td>
                  ))}
                </tr>
                <tr>
                  <td>On-Hand Inventory</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatCurrency(d.metrics.apparelInventory.onHand)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Inventory Turns</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id}>{formatNumber(d.metrics.apparelInventory.turns)}</td>
                  ))}
                </tr>

                {/* Total Inventory Variance */}
                <tr className="section-header-row">
                  <td colSpan={filteredDealerships.length + 1}>Total Inventory to Goal Variance</td>
                </tr>
                <tr>
                  <td>Total Inventory Variance</td>
                  {filteredDealerships.map(d => (
                    <td key={d.id} className={d.metrics.totalInventoryVariance < 0 ? 'negative' : 'positive'}>
                      {formatCurrency(d.metrics.totalInventoryVariance)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
